// Settlement — ranking a group and applying promotion/relegation. Pure and
// deterministic (spec §8.3, §14.6). The division ladder resets every round: the
// top promote, the bottom relegate, everyone else stays. No cumulative season
// score ever gates rank, which is what keeps the game fair to join at any time.
import { BOTTOM_TIER, CONFIG, TOP_TIER } from './config.js';
/**
 * Rank entries within a group, fully deterministically:
 *   (a) roundScore desc  →  (b) tiebreakPrimary desc (football: correct legs)  →
 *   (c) tiebreakSecondary desc (football: correct anchors)  →
 *   (d) earliest submittedAt asc  →  (e) userId asc.
 * Returns a new sorted array; does not mutate the input.
 */
export function rankGroup(entries) {
    return [...entries].sort((a, b) => b.roundScore - a.roundScore ||
        b.tiebreakPrimary - a.tiebreakPrimary ||
        b.tiebreakSecondary - a.tiebreakSecondary ||
        a.submittedAt - b.submittedAt ||
        a.userId.localeCompare(b.userId));
}
/**
 * How many of a group's players go up and down this round, scaled to the group's
 * ACTUAL size. This is the fairness fix at the heart of the ladder: PROMOTE_COUNT
 * and RELEGATE_COUNT describe a FULL division of DIVISION_SIZE, but real groups are
 * frequently smaller — a thin tier, the leftover when a tier doesn't divide evenly,
 * a brand-new league. Awarding a flat 5/5 to a 6-player group promotes almost
 * everyone, and a solo "group" would auto-promote every single round. Scaling by
 * the same FRACTION keeps a player's odds of moving independent of the
 * (shuffle-decided) size of the group they happen to land in.
 *
 * Guarantees at every size:
 *   • groupSize ≤ 2 → no movement at all — you can't meaningfully crown or drop a
 *     player in a 1–2 person division, and a solo group must NEVER auto-promote
 *     (the exact bug this replaces);
 *   • at least one player always HOLDS (`promote + relegate ≤ groupSize − 1`), so a
 *     group is never "everybody moves";
 *   • at groupSize === DIVISION_SIZE it returns exactly PROMOTE_COUNT / RELEGATE_COUNT.
 *
 * Tier caps (the top tier can't promote, the bottom can't relegate) are applied
 * later in {@link settleGroup}; this only sizes the bands.
 */
export function movementCountsForGroup(groupSize) {
    const n = Math.max(0, Math.trunc(groupSize));
    if (n <= 2)
        return { promote: 0, relegate: 0 };
    const fullSize = CONFIG.DIVISION_SIZE > 0 ? CONFIG.DIVISION_SIZE : n;
    let promote = Math.min(n, Math.max(0, Math.round((n * CONFIG.PROMOTE_COUNT) / fullSize)));
    let relegate = Math.min(n, Math.max(0, Math.round((n * CONFIG.RELEGATE_COUNT) / fullSize)));
    // Keep a hold band: trim the larger side first (ties trim relegation) until at
    // least one player stays put. With the default 20/20 fractions this never fires,
    // but it makes the function total for any CONFIG (e.g. very aggressive counts).
    while (promote + relegate > n - 1 && promote + relegate > 0) {
        if (relegate >= promote)
            relegate -= 1;
        else
            promote -= 1;
    }
    return { promote, relegate };
}
/** Promotions awarded to a group of `groupSize` (see {@link movementCountsForGroup}). */
export function promoteCountForGroup(groupSize) {
    return movementCountsForGroup(groupSize).promote;
}
/** Relegations applied to a group of `groupSize` (see {@link movementCountsForGroup}). */
export function relegateCountForGroup(groupSize) {
    return movementCountsForGroup(groupSize).relegate;
}
/**
 * Settle one group: rank it, promote the top band (capped at the top tier →
 * STAYED), relegate the bottom band (capped at the bottom tier → STAYED), everyone
 * else STAYS. Tier moves at most one rung per round.
 *
 * The bands default to {@link movementCountsForGroup} — scaled to this group's
 * size, so a partial group is settled by the same fraction as a full one. An
 * explicit `promoteCount`/`relegateCount` override is honoured verbatim (used by
 * tests and any caller that needs to force an exact band).
 */
export function settleGroup(entries, opts = {}) {
    const ranked = rankGroup(entries);
    const n = ranked.length;
    const promote = opts.promoteCount ?? promoteCountForGroup(n);
    const relegate = opts.relegateCount ?? relegateCountForGroup(n);
    return ranked.map((e, i) => {
        const rank = i + 1;
        let movement = 'STAYED';
        let nextTier = e.tier;
        // The promote band wins outright: in a tiny group a top finisher can also sit
        // inside the relegation band, but promotion (or a top-tier hold) takes priority.
        if (rank <= promote) {
            if (e.tier < TOP_TIER) {
                movement = 'PROMOTED';
                nextTier = e.tier + 1;
            }
        }
        else if (rank > n - relegate && e.tier > BOTTOM_TIER) {
            movement = 'RELEGATED';
            nextTier = e.tier - 1;
        }
        return { userId: e.userId, rankInGroup: rank, movement, nextTier, wonRound: rank === 1 };
    });
}
/** 0-based index at/after which members relegate — for drawing the line in the UI.
 *  Defaults to the size-scaled relegate count so the drawn line matches what
 *  settlement will actually do for a group of this size. */
export function relegationLineIndex(memberCount, relegateCount = relegateCountForGroup(memberCount)) {
    return memberCount - relegateCount;
}
//# sourceMappingURL=settlement.js.map